import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { highlightText, type ShjLanguage } from '@speed-highlight/core';
import { Copy, Check, Sparkle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { cn } from '~/lib/utils';
import { Tip } from '~/components/ui/tip';

interface CodeEditorProps {
  value: string;
  onChange?: (next: string) => void;
  language: ShjLanguage;
  placeholder?: string;
  rows?: number;
  readOnly?: boolean;
  className?: string;
  minHeight?: number;
  /** Show a left gutter with line numbers. */
  showLineNumbers?: boolean;
  /** Show the top-right action bar (copy / format / wrap toggle). Default true. */
  showActions?: boolean;
}

/**
 * Two-layer code editor: a transparent <textarea> on top of a syntax-
 * highlighted <pre> mirror. The trick is they MUST share identical text
 * metrics — same font-family, size, line-height, letter-spacing, padding,
 * box-sizing, tab-size — or the cursor and selection drift relative to the
 * glyphs underneath. Both layers consume the same `TEXT_STYLE` object via
 * inline `style=` to keep cascade differences out.
 *
 * Optional UI:
 *   - showLineNumbers: aria-hidden left gutter, scroll-synced with the
 *     textarea, sharing the same line-height so numbers line up with rows.
 *   - showActions: top action bar — Copy (always) + Format (JSON only,
 *     hidden when the value doesn't parse).
 *
 * Highlighting is debounced (~60ms after the last keystroke) so fast typing
 * doesn't thrash React.
 */

const FONT_STACK =
  "ui-monospace, 'SF Mono', SFMono-Regular, Menlo, Consolas, 'Liberation Mono', monospace";

// Text rendering constants — duplicated on every layer (pre, textarea,
// gutter) via inline style so cascade differences can't sneak in.
const TEXT_STYLE: CSSProperties = {
  fontFamily: FONT_STACK,
  fontSize: 12,
  lineHeight: '18px',
  letterSpacing: 0,
  wordSpacing: 'normal',
  tabSize: 2,
  fontVariantLigatures: 'none',
  padding: '0.5rem 0.75rem',
  margin: 0,
  border: 0,
  boxSizing: 'border-box',
};

// Gutter shares the metrics but has its own padding (tight on the right,
// roomy on the left). Width grows past 4 digits automatically.
const GUTTER_STYLE: CSSProperties = {
  ...TEXT_STYLE,
  padding: '0.5rem 0.5rem 0.5rem 0.75rem',
  textAlign: 'right',
  userSelect: 'none',
};

export function CodeEditor({
  value,
  onChange,
  language,
  placeholder,
  rows = 10,
  readOnly = false,
  className,
  minHeight,
  showLineNumbers = false,
  showActions = true,
}: CodeEditorProps) {
  const [html, setHtml] = useState('');
  const [copied, setCopied] = useState(false);
  const preRef = useRef<HTMLPreElement>(null);
  const gutterRef = useRef<HTMLPreElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);

  // Debounced highlight pass. Keystrokes update `value` immediately (textarea
  // is uncontrolled w.r.t. paint), but the highlighter only re-runs after
  // 60ms idle — avoids one async highlight per character on fast typing.
  useEffect(() => {
    let cancelled = false;
    const text = value || '';
    // Trailing newline keeps the <pre> height matching the textarea while
    // typing — without it, the last line gets clipped by half a line of
    // height when the cursor advances past the final newline.
    const padded = text.endsWith('\n') ? text + ' ' : text;
    const handle = window.setTimeout(() => {
      highlightText(padded, language, false)
        .then((out: string) => { if (!cancelled) setHtml(out); })
        .catch(() => { if (!cancelled) setHtml(escapeHtml(padded)); });
    }, 60);
    return () => { cancelled = true; window.clearTimeout(handle); };
  }, [value, language]);

  // Line numbers — recomputed cheaply on every value change. Memoised so the
  // gutter <pre> doesn't re-render text identical to itself.
  const lineCount = useMemo(() => {
    if (!showLineNumbers) return 0;
    return Math.max(1, value.split('\n').length);
  }, [value, showLineNumbers]);
  const gutterText = useMemo(() => {
    if (!showLineNumbers) return '';
    let out = '';
    for (let i = 1; i <= lineCount; i++) out += i + '\n';
    return out;
  }, [lineCount, showLineNumbers]);
  // Pad gutter width to fit the largest number (min 2ch).
  const gutterWidth = useMemo(() => {
    if (!showLineNumbers) return 0;
    const digits = String(lineCount).length;
    return Math.max(2, digits);
  }, [lineCount, showLineNumbers]);

  // Whether the current content parses as JSON — drives the "Format" button
  // visibility. Cheap to compute on every render; small JSON strings only.
  const isFormattableJson = useMemo(() => {
    if (language !== 'json') return false;
    const trimmed = value.trim();
    if (!trimmed) return false;
    try { JSON.parse(trimmed); return true; } catch { return false; }
  }, [value, language]);

  function syncScroll() {
    const ta = taRef.current;
    if (!ta) return;
    if (preRef.current) {
      preRef.current.scrollTop = ta.scrollTop;
      preRef.current.scrollLeft = ta.scrollLeft;
    }
    // Gutter only follows vertical scroll — horizontal stays pinned so
    // numbers don't slide off-screen.
    if (gutterRef.current) {
      gutterRef.current.scrollTop = ta.scrollTop;
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (readOnly) return;
    const ta = e.currentTarget;
    if (e.key === 'Tab') {
      e.preventDefault();
      const { selectionStart: s, selectionEnd: end } = ta;
      const next = value.slice(0, s) + '  ' + value.slice(end);
      onChange?.(next);
      requestAnimationFrame(() => {
        ta.selectionStart = ta.selectionEnd = s + 2;
      });
      return;
    }
    // Enter: copy the indentation of the current line so block structure
    // sticks. Standard editor behaviour.
    if (e.key === 'Enter') {
      const { selectionStart: s, selectionEnd: end } = ta;
      const before = value.slice(0, s);
      const lineStart = before.lastIndexOf('\n') + 1;
      const currentLine = before.slice(lineStart);
      const indentMatch = currentLine.match(/^(\s+)/);
      const indent = indentMatch ? indentMatch[1] : '';
      if (!indent) return;
      e.preventDefault();
      const insert = '\n' + indent;
      const next = value.slice(0, s) + insert + value.slice(end);
      onChange?.(next);
      requestAnimationFrame(() => {
        ta.selectionStart = ta.selectionEnd = s + insert.length;
      });
    }
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Copy failed — clipboard unavailable');
    }
  }

  function handleFormat() {
    try {
      const parsed = JSON.parse(value);
      const formatted = JSON.stringify(parsed, null, 2);
      onChange?.(formatted);
      toast.success('Formatted');
    } catch (err) {
      toast.error(`Format failed: ${(err as Error).message}`);
    }
  }

  const langClass = `shj-lang-${language}`;

  return (
    <div
      className={cn(
        'flex flex-col rounded-md border border-input bg-card overflow-hidden',
        'focus-within:ring-2 focus-within:ring-ring/40 focus-within:border-ring',
        readOnly && 'opacity-95',
        className,
      )}
      style={minHeight ? { minHeight } : undefined}
    >
      {showActions && (
        <div className="flex items-center justify-end gap-0.5 border-b border-border bg-muted/30 px-1.5 py-1 shrink-0">
          {isFormattableJson && !readOnly && (
            <ActionButton onClick={handleFormat} title="Format JSON (Prettier-style 2-space indent)">
              <Sparkle size={12} /> Format
            </ActionButton>
          )}
          <ActionButton onClick={handleCopy} title="Copy contents">
            {copied ? <Check size={12} weight="bold" /> : <Copy size={12} />}
            {copied ? 'Copied' : 'Copy'}
          </ActionButton>
        </div>
      )}

      <div className="flex flex-1 min-h-0">
        {showLineNumbers && (
          <pre
            ref={gutterRef}
            aria-hidden
            className="shrink-0 select-none text-muted-foreground/55 bg-muted/20 border-r border-border overflow-hidden"
            style={{ ...GUTTER_STYLE, width: `calc(${gutterWidth}ch + 1.25rem)` }}
          >
            {gutterText}
          </pre>
        )}
        <div className="relative flex-1 min-w-0">
          <pre
            ref={preRef}
            aria-hidden
            className={cn(langClass, 'absolute inset-0 whitespace-pre overflow-auto pointer-events-none')}
            style={TEXT_STYLE}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: html }}
          />
          <textarea
            ref={taRef}
            value={value}
            onChange={(e) => onChange?.(e.target.value)}
            onScroll={syncScroll}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            rows={rows}
            readOnly={readOnly}
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
            autoComplete="off"
            wrap="off"
            className="absolute inset-0 w-full h-full resize-none outline-none overflow-auto whitespace-pre bg-transparent text-transparent caret-foreground selection:bg-primary/35"
            style={TEXT_STYLE}
          />
        </div>
      </div>
    </div>
  );
}

function ActionButton({
  children,
  onClick,
  title,
}: {
  children: React.ReactNode;
  onClick: () => void;
  title: string;
}) {
  return (
    <Tip label={title}>
      <button
        type="button"
        onClick={onClick}
        className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[length:var(--text-2xs)] font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
      >
        {children}
      </button>
    </Tip>
  );
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' } as Record<string, string>)[c]
  );
}
